// Ensure this import is correct based on your project structure
import { AxiosResponse } from "axios";
import Holidays from "date-holidays";
import { toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";

import {
  AdjustmentPayload,
  AltiusRes,
  AmortizationData,
  CustomPayment,
  DateRange,
  DebtSegment,
  ExpectedPaymentTable,
  FacilityFee,
  ICreateDealData,
  IDeal,
  PrepaymentPayload,
  ReportFilters,
  WithdrawalPayload
} from "../../types";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IDebtActions {
  getAmortizationTableAction: (
    dealId: number,
    date?: string,
    params?: { split_linkage?: boolean; currency?: string; exchange_date?: string }
  ) => Promise<AmortizationData>;
  createDebtSegmentAction: (dealId: number, data: DebtSegment) => Promise<void>;
  updateDebtSegmentAction: (data: { dealId: number; segment: DebtSegment }) => Promise<void>;
  updateBulkDebtSegmentsAction: (data: {
    dealId: number;
    segments: DebtSegment[];
    fees?: FacilityFee[];
  }) => Promise<void>;
  createCustomPaymentsAction: (dealId: number, payload: Partial<CustomPayment>[]) => Promise<void>;
  updateCustomPaymentsAction: (
    dealId: number,
    customPaymentId: number,
    payload: PrepaymentPayload
  ) => Promise<CustomPayment>;
  removeCustomPaymentAction: (data: { dealId: number; date: string }) => Promise<void>;
  createDealWithSegmentAction: (
    dealData: Pick<ICreateDealData, "title" | "asset_class_id" | "sub_asset_class_id" | "template_id" | "type">,
    segments: DebtSegment[]
  ) => Promise<{ dealId: number; dealTitle: string }>;
  getDebtIndexValueAction: (index: string, date: string) => Promise<number>;
  getExpectedPayments: (body: {
    asset_class_ids?: number[];
    deal_ids?: number[];
    date?: DateRange | null;
  }) => Promise<ExpectedPaymentTable>;
  getBalanceApprovalAction: (data: { dealId: number; type: string; report_date?: string }) => Promise<void>;
  withdrawalsAskAIAction: (data: { dealId: number; file?: File; text?: string }) => Promise<Partial<DebtSegment>>;
  downloadAmortizationTableAction: (data: {
    dealId: number;
    date?: string;
    from_date?: string;
    to_date?: string;
    split_linkage?: boolean;
    currency?: string;
    exchange_date?: string;
  }) => Promise<void>;
  createPrepaymentAction: (dealId: number, payload: PrepaymentPayload) => Promise<CustomPayment>;
  getCombinedAmortizationTable: (data: {
    filters: ReportFilters;
    currency?: string;
    exchange_date?: string;
  }) => Promise<AmortizationData["table"]>;
  createWithdrawalAction: (
    dealId: number,
    payload: WithdrawalPayload
  ) => Promise<AltiusRes<DebtSegment>>;
  createAdjustmentAction: (dealId: number, payload: AdjustmentPayload) => Promise<void>;
  updateAdjustmentAction: (dealId: number, adjustmentId: number, payload: AdjustmentPayload) => Promise<void>;
  importDebtAction: (files: File[]) => Promise<void>;
}

export class DebtActions extends AsyncActions<IDebtActions> implements IDebtActions {
  constructor(params: AsyncActionsParams) {
    super(params);
  }

  private applyHolidayMarkings = (rows: AmortizationData["table"]["rows"]) => {
    const USA = new Holidays("US");
    const BG = new Holidays("GB");

    return rows.map((row) => {
      const checkDate = new Date(row.date);
      const dayOfWeek = checkDate.getDay();

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return { ...row, is_holiday: true };
      }

      const checkedDate = USA.isHoliday(checkDate) || BG.isHoliday(checkDate);
      const isBankHoliday = checkedDate && checkedDate[0]?.type === "bank";
      return { ...row, is_holiday: isBankHoliday };
    });
  };

  public getAmortizationTableAction = async (
    dealId: number,
    date?: string,
    params?: { split_linkage?: boolean; currency?: string; exchange_date?: string }
  ): Promise<AmortizationData> => {
    let url = `/api/v0.0.3/deals/${dealId}/debt/amortization-table?report_date=${date}`;

    Object.entries(params || {}).forEach(([key, value]) => {
      url += `&${key}=${value?.toString()}`;
    });

    const {
      data: { data }
    } = await this.api.get<AltiusRes<AmortizationData>>({
      url,
      signal: this.addProcess("getAmortizationTableAction").signal
    });

    data.table.rows = this.applyHolidayMarkings(data.table.rows);

    this.removeProcess("getAmortizationTableAction");
    return data;
  };

  public createDebtSegmentAction = async (dealId: number, data: DebtSegment): Promise<void> => {
    await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/debt/segments`,
      data,
      signal: this.addProcess("createDebtSegmentAction").signal
    });

    this.removeProcess("createDebtSegmentAction");
  };

  public updateDebtSegmentAction = async ({
    dealId,
    segment
  }: {
    dealId: number;
    segment: DebtSegment;
  }): Promise<void> => {
    await this.api.patch({
      url: `/api/v0.0.3/deals/${dealId}/debt/segments/${segment.id}`,
      data: segment
    });
  };

  public updateBulkDebtSegmentsAction = async ({
    dealId,
    segments,
    fees
  }: {
    dealId: number;
    segments: (DebtSegment & { custom_payments_file?: File })[];
    fees?: FacilityFee[];
  }): Promise<void> => {
    const segmentsToSend = segments.map((segment) => ({ ...segment, id: segment.id || undefined }));
    const feesToSend = fees?.map((fee) => ({ ...fee, id: fee.id || undefined }));

    await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/debt/segments-bulk`,
      data: { segments: segmentsToSend, fees: feesToSend },
      signal: this.addProcess("updateBulkDebtSegmentsAction").signal
    });

    const segmentsWithCustomPayments = segments.filter((segment) => segment.custom_payments_file);
    if (segmentsWithCustomPayments.length) {
      await this.uploadCustomPaymentsCSVAction(dealId, segmentsWithCustomPayments[0].custom_payments_file!);
    }

    this.removeProcess("updateBulkDebtSegmentsAction");
  };

  public uploadCustomPaymentsCSVAction = async (dealId: number, file: File): Promise<void> => {
    const formData = new FormData();

    formData.append("file", file);
    await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/debt/import-custom-payments`,
      data: formData,
      signal: this.addProcess("uploadCustomPaymentsCSVAction").signal,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });

    this.removeProcess("uploadCustomPaymentsCSVAction");
  };

  public createCustomPaymentsAction = async (dealId: number, payload: Partial<CustomPayment>[]): Promise<void> => {
    await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/debt/custom-payments`,
      data: payload,
      signal: this.addProcess("createCustomPaymentsAction").signal
    });

    this.removeProcess("createCustomPaymentsAction");
  };

  public updateCustomPaymentsAction = async (
    dealId: number,
    customPaymentId: number,
    payload: PrepaymentPayload
  ): Promise<CustomPayment> => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<CustomPayment>>({
      url: `/api/v0.0.3/deals/${dealId}/debt/custom-payments/${customPaymentId}`,
      data: payload,
      signal: this.addProcess("updateCustomPaymentsAction").signal
    });

    this.removeProcess("updateCustomPaymentsAction");

    return data;
  };

  public removeCustomPaymentAction = async ({ dealId, date }: { dealId: number; date: string }): Promise<void> => {
    await this.api.delete({
      url: `/api/v0.0.3/deals/${dealId}/debt/custom-payments/${date}`,
      signal: this.addProcess("removeCustomPaymentAction").signal
    });

    this.removeProcess("removeCustomPaymentAction");
  };

  public createDealWithSegmentAction = async (
    dealData: Pick<ICreateDealData, "title" | "asset_class_id" | "sub_asset_class_id" | "template_id" | "type">,
    segments: DebtSegment[]
  ): Promise<{ dealId: number; dealTitle: string }> => {
    // Step 1: Create the deal
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<IDeal>>({
      url: "/api/v0.0.2/deals",
      data: dealData
    });

    const dealId = payload.id;
    const dealTitle = payload.title;

    // Step 2: Create segments for the newly created deal
    await this.updateBulkDebtSegmentsAction({
      dealId,
      segments
    });

    return { dealId, dealTitle };
  };

  public getDebtIndexValueAction = async (index: string, date: string): Promise<number> => {
    const {
      data: { data }
    } = await this.api.get<AxiosResponse<number>>({
      url: `/api/v0.0.3/index-value/${index}/${date}`,
      signal: this.addProcess("getDebtIndexValueAction").signal
    });

    this.removeProcess("getDebtIndexValueAction");
    return data;
  };

  public getExpectedPayments: IDebtActions["getExpectedPayments"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AxiosResponse<ExpectedPaymentTable>>({
      url: "/api/v0.0.3/debt/expected-payments",
      data: body,
      signal: this.addProcess("getExpectedPayments").signal
    });

    this.removeProcess("getExpectedPayments");
    return data;
  };

  public getBalanceApprovalAction: IDebtActions["getBalanceApprovalAction"] = async (data) => {
    const { dealId, type = "facility", report_date } = data;
    await this.api
      .get({
        url: `/api/v0.0.3/deals/${dealId}/debt-pdf`,
        params: {
          type,
          report_date
        },
        signal: this.addProcess("getBalanceApproval").signal,
        responseType: "blob"
      })
      .then(downloadFileAxios)
      .catch(async (error) => {
        const parsed = JSON.parse(await error?.response?.text());
        const errors = parsed?.errors as Record<string, string[]> | undefined;
        const message = errors ? Object.values(errors).flat().join(", ") : (parsed?.message ?? "An error occurred");
        throw new Error(message);
      });
  };

  public withdrawalsAskAIAction: IDebtActions["withdrawalsAskAIAction"] = async ({ dealId, ...body }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DebtSegment>>({
      url: `/api/v0.0.3/deals/${dealId}/debt/withdrawals-ask-ai`,
      data: toFormData({
        ...body
      }),
      headers: {
        "Content-Type": "multipart/form-data"
      },
      signal: this.addProcess("withdrawalsAskAIAction").signal
    });

    return data;
  };

  public downloadAmortizationTableAction: IDebtActions["downloadAmortizationTableAction"] = async (data) => {
    const { dealId, ...params } = data;
    await this.api
      .get({
        url: `/api/v0.0.3/deals/${dealId}/debt/amortization-table/export`,
        params,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public createPrepaymentAction: IDebtActions["createPrepaymentAction"] = async (dealId, payload) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<CustomPayment>>({
      url: `/api/v0.0.3/deals/${dealId}/debt/custom-payments`,
      data: payload,
      signal: this.addProcess("createPrepaymentAction").signal
    });

    this.removeProcess("createPrepaymentAction");
    return data;
  };

  public getCombinedAmortizationTable: IDebtActions["getCombinedAmortizationTable"] = async (data) => {
    const {
      data: { data: responseData }
    } = await this.api.post<AltiusRes<AmortizationData["table"]>>({
      url: "/api/v0.0.3/debt/amortization-table",
      data: {
        filters: data.filters,
        currency: data.currency,
        exchange_date: data.exchange_date
      },
      signal: this.addProcess("getCombinedAmortizationTable").signal
    });

    responseData.rows = this.applyHolidayMarkings(responseData.rows);

    this.removeProcess("getCombinedAmortizationTable");
    return responseData;
  };

  public createWithdrawalAction: IDebtActions["createWithdrawalAction"] = async (dealId, payload) => {
    const response = await this.api.post<AltiusRes<DebtSegment>>({
      url: `/api/v0.0.3/deals/${dealId}/debt/withdrawals`,
      data: payload,
      signal: this.addProcess("createWithdrawalAction").signal
    });

    this.removeProcess("createWithdrawalAction");
    return response.data;
  };

  public createAdjustmentAction: IDebtActions["createAdjustmentAction"] = async (dealId, payload) => {
    await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/debt/adjustments`,
      data: payload,
      signal: this.addProcess("createAdjustmentAction").signal
    });

    this.removeProcess("createAdjustmentAction");
  };

  public updateAdjustmentAction: IDebtActions["updateAdjustmentAction"] = async (dealId, adjustmentId, payload) => {
    await this.api.patch({
      url: `/api/v0.0.3/deals/${dealId}/debt/adjustments/${adjustmentId}`,
      data: payload,
      signal: this.addProcess("updateAdjustmentAction").signal
    });

    this.removeProcess("updateAdjustmentAction");
  };

  public importDebtAction: IDebtActions["importDebtAction"] = async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append(file.name.replace(/\.[^.]+$/, ""), file));

    await this.api.post({
      url: "/api/v0.0.3/debt/import",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
  };
}
